import React from 'react';
import './StupidSystemDesigns.css';

const fileSystemDesignCode = String.raw`#include <bits/stdc++.h>
using namespace std;

struct Node {
    bool isFile;
    string contents = "";
    map<string, Node*> children;

    Node() {
        isFile = false;
        contents = "";
    }
    
    ~Node() {
        for (auto& pair : children) {
            delete pair.second;
        }
    }
};

class FileSystem {
    Node* root;

    vector<string> parsePath(string path) {
        stringstream ss(path);
        vector<string> pathArr;
        string subpath;
        while (getline(ss, subpath, '/')) {
            if (subpath != "") pathArr.push_back(subpath);
        }
        return pathArr;
    }

    Node* getNode(string path) {
        vector<string> pathArr = parsePath(path);
        Node* cur = root;
        for (auto& subPath : pathArr) {
            if (cur->children.find(subPath) == cur->children.end()) {
                return nullptr;
            }
            cur = cur->children[subPath];
        }
        return cur;
    }

    Node* getParent(string path) {
        vector<string> pathArr = parsePath(path);
        if (pathArr.empty()) return nullptr;
        
        Node* parentNode = root;
        for (int i = 0; i < pathArr.size() - 1; i++) {
            if (parentNode->children.find(pathArr[i]) == parentNode->children.end()) return nullptr;
            parentNode = parentNode->children[pathArr[i]];
        }
        return parentNode;
    }

    void dfs(Node* cur, string& name, vector<string>& paths, string path) {
        if (!cur) return;
        for (auto& pairs : cur->children) {
            string nextPath = (path == "/") ? "/" + pairs.first : path + "/" + pairs.first;
            if (pairs.first == name) {
                paths.push_back(nextPath);
            }
            if (!pairs.second->isFile) {
                dfs(pairs.second, name, paths, nextPath);
            }
        }
    }

public:
    FileSystem() {
        root = new Node();
    }
    
    ~FileSystem() {
        delete root;
    }

    // LINUX: ls
    vector<string> ls(string path) {
        Node* node = getNode(path);
        if (!node) return {};
        if (node->isFile) {
            vector<string> nodePath = parsePath(path);
            return {nodePath.back()};
        } else {
            vector<string> nodeChildren;
            for (auto& pairs : node->children) {
                nodeChildren.push_back(pairs.first);
            }
            return nodeChildren;
        }
    }

    // LINUX: mkdir
    void mkdir(string path) {
        vector<string> pathArr = parsePath(path);
        Node* cur = root;
        for (auto& subpath : pathArr) {
            if (cur->children.find(subpath) == cur->children.end()) {
                cur->children[subpath] = new Node();
            }
            cur = cur->children[subpath];
        }
    }

    // LINUX: touch (creates an empty file)
    bool touch(string path) {
        vector<string> pathArr = parsePath(path);
        if (pathArr.empty()) return false;
        
        Node* cur = root;
        for (auto& subpath : pathArr) {
            if (cur->children.find(subpath) == cur->children.end()) {
                cur->children[subpath] = new Node();
            }
            cur = cur->children[subpath];
        }
        
        if (!cur->isFile && cur->contents != "") return false;
        cur->isFile = true;
        return true;
    }

    // LINUX: echo (writes or appends content to a file)
    bool echo(string path, string newContent, bool append = false) {
        Node* node = getNode(path);
        if (!node || !node->isFile) return false;
        
        if (append) node->contents += newContent; // equivalent to 'echo "text" >> file'
        else node->contents = newContent;         // equivalent to 'echo "text" > file'
        
        return true;
    }

    // LINUX: cat (reads file contents)
    string cat(string path) {
        Node* node = getNode(path);
        if (node && node->isFile) return node->contents;
        return "";
    }

    // LINUX: rm (removes files or directories)
    bool rm(string path) {
        if (path == "/" || path.empty()) return false;
        
        Node* parent = getParent(path);
        if (!parent) return false;
        
        vector<string> pathArr = parsePath(path);
        string targetName = pathArr.back();
        
        if (parent->children.find(targetName) != parent->children.end()) {
            delete parent->children[targetName];
            parent->children.erase(targetName);
            return true;
        }
        return false;
    }

    // LINUX: rename
    bool rename(string path1, string newName) {
        if (path1 == "/" || path1.empty()) return false;
        
        Node* parentNode = getParent(path1);
        if (!parentNode) return false;
        
        vector<string> pathArr = parsePath(path1);
        string oldName = pathArr.back();
        
        if (parentNode->children.find(oldName) == parentNode->children.end()) return false;
        if (parentNode->children.find(newName) != parentNode->children.end()) return false;
        
        Node* node = parentNode->children[oldName];
        parentNode->children.erase(oldName);
        parentNode->children[newName] = node;
        return true;
    }

    // LINUX: mv
    bool mv(string path1, string path2) {
        if (path1 == "/" || path1.empty()) return false;
        
        Node* node1 = getNode(path1);
        Node* parentNode1 = getParent(path1);
        Node* node2 = getNode(path2);
        
        if (!node1 || !node2 || node2->isFile || !parentNode1) return false;
        
        vector<string> path1Arr = parsePath(path1);
        string name1 = path1Arr.back();
        
        if (node2->children.find(name1) != node2->children.end()) return false;
        
        node2->children[name1] = node1;
        parentNode1->children.erase(name1);
        return true;
    }

    // LINUX: find
    vector<string> find(string name) {
        vector<string> paths;
        dfs(root, name, paths, "/");
        return paths;
    }
};

int main() {
    FileSystem fs;

    cout << "1. Creating directories (mkdir)..." << endl;
    fs.mkdir("/company/engineering/backend");
    fs.mkdir("/company/engineering/frontend");
    
    cout << "2. Creating and writing to files (touch & echo)..." << endl;
    fs.touch("/company/engineering/backend/api.txt");
    fs.echo("/company/engineering/backend/api.txt", "Initial API code.", false); // Overwrite (>)
    fs.echo("/company/engineering/backend/api.txt", " Added auth endpoints.", true); // Append (>>)
    
    cout << "\n--- Reading File (cat) ---" << endl;
    cout << "/company/engineering/backend/api.txt: " 
         << fs.cat("/company/engineering/backend/api.txt") << endl;
         
    cout << "\n--- LS Root (ls) ---" << endl;
    for (string s : fs.ls("/")) cout << s << " "; 
    cout << endl;

    cout << "\n--- LS Engineering (ls) ---" << endl;
    for (string s : fs.ls("/company/engineering")) cout << s << " "; 
    cout << endl;

    cout << "\n3. Testing Rename & Move (rename & mv)..." << endl;
    fs.rename("/company/engineering/backend/api.txt", "server.txt");
    fs.mv("/company/engineering/backend/server.txt", "/company/engineering/frontend");
    
    cout << "\n--- Find 'server.txt' (find) ---" << endl;
    vector<string> foundPaths = fs.find("server.txt");
    for (string p : foundPaths) cout << p << endl; 

    cout << "\n4. Testing Remove (rm)..." << endl;
    fs.rm("/company/engineering"); 
    
    cout << "\n--- LS Company After rm (ls) ---" << endl;
    vector<string> postRm = fs.ls("/company");
    if (postRm.empty()) cout << "Directory is empty!" << endl;
    else for (string s : postRm) cout << s << " ";
    cout << endl;

    return 0;
}
    
`;

const stupidFileSystemDesignCode = String.raw`
#include <iostream>
#include <vector>
#include <string>
#include <algorithm>
#include <ctime>

using namespace std;

struct Node {
    string name;
    string content;
};

class StupidFS {
private:
    vector<Node*> storage;

public:
    StupidFS() {
        srand(time(0));
    }

    ~StupidFS() {
        for (size_t i = 0; i < storage.size() / 2; i++) {
            delete storage[i];
        }
    }

    void touch(string path) {
        storage.push_back(new Node{path, ""});
        
        random_shuffle(storage.begin(), storage.end());

        if (rand() % 10 == 0 && !storage.empty()) {
            storage.pop_back(); 
        }
    }

    void echo(string path, string data) {
        for (auto* node : storage) {
            if (node->name == path) {
                if (rand() % 5 == 0) {
                    node->content = "corrupted";
                } else {
                    node->content = data;
                }
                return;
            }
        }
    }

    string cat(string path) {
        if (rand() % 2 == 0) {
            return "not found";
        }
        
        for (auto* node : storage) {
            if (node->name == path) return node->content;
        }
        return "not found";
    }

    vector<string> ls() {
        vector<string> visible_files;
        for (auto* node : storage) {
            if (rand() % 10 > 2) {
                visible_files.push_back(node->name);
            }
        }
        return visible_files;
    }
};

int main() {
    StupidFS fs;
    
    fs.touch("resume.pdf");
    fs.touch("keys.txt");
    fs.touch("photos.jpeg");
    
    fs.echo("keys.txt", "secret_key_123");

    for (string file : fs.ls()) {
        cout << file << endl;
    }
    
    cout << fs.cat("keys.txt") << endl;

    return 0;
}
`;

function FileSystemDesign({ theme, onBackClick }) {
    return (
        <div className={`app ${theme}`}>
            <main className="system-design-page">
                <section className="system-designs-header-section">
                    <div className="system-designs-header">
                        <div className="system-designs-header-copy">
                            <h1>File System Design</h1>
                            <p className="system-designs-subtitle">
                                A simple low-level design for storing files as nodes and controlling them with familiar Linux commands.
                            </p>
                        </div>
                        <button className="back-btn" onClick={onBackClick}>Back to Designs</button>
                    </div>
                </section>

                <section className="system-designs-content">
                    <div className="system-design-article">
                        <div className="design-section">
                            <h2>How It Works</h2>
                            <p className="system-design-description">
                                Think of the file system as a tree. Each file or folder is a node, and folders keep a list of their child nodes.
                                The controller starts at the root folder, follows a path, and performs the requested operation. (similar to Trie data structure)
                            </p>
                        </div>

                        <div className="design-section">
                            <h2>Main Building Blocks</h2>
                            <ul>
                                <li><strong>Node:</strong> Represents either a file or a directory. It stores its name, type, and child nodes when needed.</li>
                                <li><strong>File system controller:</strong> Finds nodes by path and handles creating, moving, reading, and deleting them.</li>
                            </ul>
                        </div>

                        <div className="design-section">
                            <h2>Supported Commands</h2>
                            <ul>
                                <li><code>ls</code> lists the contents of a directory.</li>
                                <li><code>cd</code> changes the current directory and <code>pwd</code> prints its path.</li>
                                <li><code>touch</code> creates a file and <code>mkdir</code> creates a directory.</li>
                                <li><code>mv</code> moves or renames a node, while <code>rm</code> removes it.</li>
                                <li><code>cat</code> reads a file's contents.</li>
                            </ul>
                        </div>

                        <div className="design-section stupid-design-section">
                            <h2>Stupid Design</h2>
                            <h3>Key Architectural &quot;Innovations&quot;</h3>
                            <ul>
                                <li>Hierarchies are a myth. <code>/usr/local/bin</code> and <code>notes.txt</code> are dumped into the exact same unsorted vector.</li>
                                <li>Saving a new file triggers a <code>random_shuffle</code> on the entire storage vector, fragmenting the data layout instantly.</li>
                                <li>The system has a built-in 10% chance to permanently erase a random file without freeing its memory every time a new file is created.</li>
                                <li>A file may exist in memory, but it only has a 70% chance of actually appearing when you list the directory.</li>
                            </ul>
                        </div>

                        <div className="design-section stupid-design-section">
                            <div className="design-code-header">
                                <h2>Stupid C++ Sample Code</h2>
                            </div>
                            <pre className="design-code-block"><code>{stupidFileSystemDesignCode}</code></pre>
                        </div>

                        <div className="design-section">
                            <div className="design-code-header">
                                <h2>Working Logic</h2>
                            </div>
                            <pre className="design-code-block"><code>{fileSystemDesignCode}</code></pre>
                        </div>
                    </div>

                    <div className="system-designs-footer-note">And thats a small low-level design for file systems folks. By your very own World's Okayest Software Developer!</div>
                    <button className="back-btn" onClick={onBackClick}>Back to Designs</button>
                </section>
            </main>
            <footer className="footer">
                <p>© 2026 StupidDesigns.com. All rights reserved. Not that anyone actually wants them.</p>
            </footer>
        </div>
    );
}

export default FileSystemDesign;
